import { toast as toastify } from 'react-toastify';
import type { ToastOptions, Id } from 'react-toastify';

import Icons from 'assets/icons';

interface ToastContentProps {
  title?: string;
  message?: string;
  children?: React.ReactNode;
}

type ToastCallProps = ToastContentProps & ToastOptions;

type ToastMethod = (props: ToastCallProps) => Id;

type CustomToast = ToastMethod &
  Omit<typeof toastify, 'error' | 'info' | 'success' | 'warning'> & {
    error: ToastMethod;
    info: ToastMethod;
    success: ToastMethod;
    warning: ToastMethod;
    neutral: ToastMethod;
  };

const toast = ((props: ToastCallProps): Id => toastify(<Toast {...props} />, { ...props })) as CustomToast;

const iconClassName = 'w-6 h-6';

Object.keys(toastify).forEach((key) => {
  (toast as unknown as Record<string, unknown>)[key] = (toastify as unknown as Record<string, unknown>)[key];
});

toast.error = (props: ToastCallProps): Id =>
  toastify.error(<Toast {...props} />, {
    ...props,
    icon: <Icons.Warning className={iconClassName} />,
  });

toast.info = (props: ToastCallProps): Id =>
  toastify.info(<Toast {...props} />, {
    ...props,
    icon: <Icons.Info className={iconClassName} />,
  });

toast.success = (props: ToastCallProps): Id =>
  toastify.success(<Toast {...props} />, {
    ...props,
    icon: <Icons.Success className={iconClassName} />,
  });

toast.warning = (props: ToastCallProps): Id =>
  toastify.warning(<Toast {...props} />, {
    ...props,
    icon: <Icons.Warning className={iconClassName} />,
  });

toast.neutral = (props: ToastCallProps): Id =>
  toastify.info(<Toast {...props} />, {
    ...props,
    icon: <Icons.Info className={iconClassName} />,
  });

const Toast = ({ title, message, children }: ToastContentProps): React.JSX.Element => (
  <div className="space-y-4" data-testid="toast">
    <div className="text-default space-y-1 leading-6">
      {title && (
        <p
          className="font-bold"
          data-testid="toast-title"
        >
          {title}
        </p>
      )}
      {message && (
        <p
          className="text-gray-600"
          data-testid="toast-message"
        >
          {message}
        </p>
      )}
    </div>
    {children && <div>{children}</div>}
  </div>
);

export default toast;
